// Activity feed (ADR 0041).

import { useInfiniteQuery } from "@tanstack/react-query";

import { api } from "./client";
import type { ActivityEntry } from "./types";
import { errorDetail } from "@/lib/apiError";

export type ActivityBy = "all" | "others";

interface ActivityPage {
  items: ActivityEntry[];
  next_cursor: string | null;
}

export const activityKeys = {
  all: ["activity"] as const,
  mine: (by: ActivityBy) => [...activityKeys.all, "mine", by] as const,
  item: (fileId: string, by: ActivityBy) =>
    [...activityKeys.all, "item", fileId, by] as const,
};

/** Your feed, or one item's (``fileId``), newest first, a page at a time. */
export function useActivity(
  fileId: string | null,
  by: ActivityBy = "all",
  pageSize = 30,
) {
  return useInfiniteQuery({
    queryKey: fileId ? activityKeys.item(fileId, by) : activityKeys.mine(by),
    initialPageParam: null as string | null,
    getNextPageParam: (last: ActivityPage) => last.next_cursor,
    refetchInterval: 60_000,
    queryFn: async ({ pageParam }): Promise<ActivityPage> => {
      const query = {
        by,
        limit: pageSize,
        ...(pageParam ? { cursor: pageParam } : {}),
      };
      const { data, error } = fileId
        ? await api.GET("/api/v1/files/{file_id}/activity", {
            params: { path: { file_id: fileId }, query },
          })
        : await api.GET("/api/v1/activity", { params: { query } });
      if (error || !data)
        throw new Error(errorDetail(error, "could not load activity"));
      return data;
    },
  });
}
